import { useQuery } from '@tanstack/react-query';
import './indent-sheet.css';
import { api } from '../../lib/api';

/**
 * Every container of ONE indent, one row each, with everything that has happened to it on the
 * inspection side: the surveyor's Stage 3 inspection, the 3.1 / 3.2 sign-offs, the depot
 * gate-in / gate-out checks, the CRO and the stock entry. A plain bordered grid (like a
 * spreadsheet) — used under an indent row in the indents list and on the indent page.
 */
interface Row {
  containerNo: string; label: string; productCode: string; status: string;
  survey: { at: string; surveyor: string; kind: string; condition: string; remarks: string; report: string; folder: string; ok: number; issues: string[]; photos: number } | null;
  approvals: Array<{ stage: string; name: string; status: string; at: string; comment: string }>;
  gateIn: Gate | null; gateOut: Gate | null;
  cro: { done: boolean; ref: string; destination: string };
  inStock: boolean; stockVia: string;
}
interface Gate { at: string; by: string; location: string; pti: string; repair: string; repairBudget: string; photos: number; report: string; remarks: string }
interface Sheet {
  indentId: string; containers: Row[];
  indentWideApprovals: Array<{ stage: string; name: string; status: string; at: string; comment: string }>;
  totals: { containers: number; surveyed: number; withIssues: number; signedOff: number; gatedIn: number; croDone: number; inStock: number };
  sheetError: string;
}

const d = (v: string) => { const t = String(v || '').slice(0, 10); return /^\d{4}-\d{2}-\d{2}$/.test(t) ? `${t.slice(8, 10)}/${t.slice(5, 7)}/${t.slice(2, 4)}` : (v ? String(v).slice(0, 10) : ''); };
const ok = (t: string) => <span className="font-semibold text-emerald-700">{t}</span>;
const bad = (t: string) => <span className="font-semibold text-rose-700">{t}</span>;
const warn = (t: string) => <span className="font-semibold text-amber-700">{t}</span>;
const none = <span className="text-slate-300">—</span>;
const link = (href: string, t: string) => (href ? <a href={href} target="_blank" rel="noreferrer" className="text-blue-700 hover:underline" onClick={(e) => e.stopPropagation()}>{t}</a> : null);

function signOff(r: Row, stage: '3.1' | '3.2') {
  const a = r.approvals.find((x) => x.stage.includes(stage));
  if (!a) return none;
  const good = /^approved$/i.test(a.status);
  return <span title={`${a.name}${a.at ? ` · ${d(a.at)}` : ''}${a.comment ? ` — ${a.comment}` : ''}`}>{good ? ok('✓') : bad('✗')} {a.name.split(' ')[0]}</span>;
}

export default function IndentInspectionSheet({ indentId }: { indentId: string }) {
  const q = useQuery<Sheet>({
    queryKey: ['procurement', 'indent', indentId, 'inspection-sheet'],
    queryFn: () => api.get(`/api/procurement/indents/${encodeURIComponent(indentId)}/inspection-sheet`),
    staleTime: 60_000,
  });
  if (q.isLoading) return <div className="p-2 text-[11px] text-slate-500">Reading the inspection records…</div>;
  if (q.isError || !q.data) return <div className="p-2 text-[11px] text-rose-700">Could not load the inspection details for {indentId}.</div>;
  const { containers, totals, indentWideApprovals, sheetError } = q.data;
  if (!containers.length) return <div className="p-2 text-[11px] text-slate-500">No containers on {indentId} yet.</div>;
  const other = (r: Row) => r.approvals.filter((a) => !a.stage.includes('3.1') && !a.stage.includes('3.2'));

  return (
    <div className="isheet overflow-x-auto bg-white">
      <table>
        <thead>
          <tr>
            <th rowSpan={2}>#</th><th rowSpan={2}>Container</th><th rowSpan={2}>Code</th><th rowSpan={2}>Status</th>
            <th colSpan={6}>Surveyor inspection (Stage 3)</th><th colSpan={3}>Sign-off</th>
            <th colSpan={4}>Gate-in</th><th rowSpan={2}>Gate-out</th><th rowSpan={2}>CRO</th><th rowSpan={2}>Stock</th>
          </tr>
          <tr>
            <th>Date</th><th>By</th><th>Condition</th><th>OK</th><th>Issues</th><th>Photos / report</th>
            <th>3.1</th><th>3.2</th><th>Other</th>
            <th>Date</th><th>PTI</th><th>Repair</th><th>Report</th>
          </tr>
        </thead>
        <tbody>
          {containers.map((r, idx) => {
            const sv = r.survey;
            return (
              <tr key={`${r.containerNo}|${r.label}|${idx}`}>
                <td className="text-slate-400">{idx + 1}</td>
                <td className="mono">{r.containerNo || r.label}</td>
                <td className="mono">{r.productCode || none}</td>
                <td>{r.status || none}</td>
                {sv ? (
                  <>
                    <td>{d(sv.at) || none}</td>
                    <td>{sv.surveyor || none}</td>
                    <td>{sv.condition || none}</td>
                    <td>{ok(String(sv.ok))}</td>
                    <td className="issues" title={sv.issues.join('\n')}>
                      {sv.issues.length ? <>{bad(`✗ ${sv.issues.length}`)} {sv.issues.slice(0, 3).join(' · ')}{sv.issues.length > 3 ? ` · +${sv.issues.length - 3}` : ''}</> : none}
                      {sv.remarks && <div className="italic text-slate-500">“{sv.remarks}”</div>}
                    </td>
                    <td>{sv.photos ? `${sv.photos} ` : ''}{link(sv.report, 'report ↗')} {link(sv.folder, 'photos ↗')}{!sv.photos && !sv.report && !sv.folder ? none : null}</td>
                  </>
                ) : (
                  <td colSpan={6} className="text-slate-400">no survey filed</td>
                )}
                <td>{signOff(r, '3.1')}</td>
                <td>{signOff(r, '3.2')}</td>
                <td>{other(r).length ? other(r).map((a, i) => <span key={i} className="mr-1" title={a.name}>{/^approved$/i.test(a.status) ? ok('✓') : bad('✗')} {a.name.split(' ')[0]}</span>) : none}</td>
                {r.gateIn ? (
                  <>
                    <td title={r.gateIn.location}>{d(r.gateIn.at) || none}</td>
                    <td>{/^yes$/i.test(r.gateIn.pti) ? ok('Yes') : r.gateIn.pti ? <span>{r.gateIn.pti}</span> : none}</td>
                    <td>{/^yes$/i.test(r.gateIn.repair) ? warn(`Needed${r.gateIn.repairBudget ? ` ₹${r.gateIn.repairBudget}` : ''}`) : r.gateIn.repair ? ok('None') : none}</td>
                    <td>{r.gateIn.photos ? `${r.gateIn.photos} ph ` : ''}{link(r.gateIn.report, 'report ↗') || (!r.gateIn.photos ? none : null)}</td>
                  </>
                ) : (
                  <td colSpan={4} className="text-slate-400">{r.cro.destination === 'direct' ? 'direct — no gate-in' : 'not gated in'}</td>
                )}
                <td>{r.gateOut ? <span title={r.gateOut.location}>{d(r.gateOut.at)}</span> : none}</td>
                <td>{r.cro.done ? <span title={r.cro.ref}>{ok('✓')} {r.cro.destination === 'direct' ? 'direct' : r.cro.destination === 'yard' ? 'yard' : ''}</span> : warn('pending')}</td>
                <td>{r.inStock ? ok(`In stock${r.stockVia ? ` (${r.stockVia})` : ''}`) : none}</td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={4}>Total · {totals.containers} containers</td>
            <td colSpan={6}>{totals.surveyed} surveyed{totals.withIssues ? ` · ${totals.withIssues} with issues` : ''}</td>
            <td colSpan={3}>{totals.signedOff} signed off</td>
            <td colSpan={4}>{totals.gatedIn} gated in</td>
            <td>—</td><td>{totals.croDone} CRO</td><td>{totals.inStock} in stock</td>
          </tr>
        </tfoot>
      </table>
      {indentWideApprovals.length > 0 && (
        <div className="border border-t-0 border-slate-300 px-2 py-1 text-[11px] text-slate-600">
          Sign-offs not tied to one container: {indentWideApprovals.map((a, i) => <span key={i} className="mr-2">{/^approved$/i.test(a.status) ? ok('✓') : bad('✗')} {a.stage ? `${a.stage} ` : ''}{a.name}{a.at ? ` ${d(a.at)}` : ''}</span>)}
        </div>
      )}
      {sheetError && <div className="px-2 py-1 text-[11px] text-amber-700">The survey sheet could not be read just now ({sheetError}) — survey columns may be blank.</div>}
    </div>
  );
}
